/**
 * The report and forecast reads, as the browser makes them (P7-T2).
 *
 * It is a small module rather than an addition to `client.ts` on purpose: the two reads are the
 * reporting surface's whole wire, they need nothing the ledger client already holds, and keeping
 * them apart means the category and entry surfaces can change without this file being touched (and
 * the other way round).
 *
 * Responses are validated with `@saldo/contracts`' precompiled validators — never by compiling a
 * schema — so a wire change is a named `contract` failure here instead of something the screen
 * renders. A refusal is classified with the ledger client's own `classifyLedgerFailure`, so a
 * `401`, a `404` and a `500` mean the same thing to this surface as to every other one.
 */

import {
  parseErrorEnvelope,
  parseLedgerForecastEnvelope,
  parseLedgerReportsEnvelope,
  type LedgerForecastEnvelope,
  type LedgerReportsEnvelope,
} from '@saldo/contracts';

import { LedgerRequestError, classifyLedgerFailure } from './client';

export async function fetchReports(path: string, signal?: AbortSignal): Promise<LedgerReportsEnvelope> {
  const answer = await send(path, signal);
  try {
    return parseLedgerReportsEnvelope(answer.body);
  } catch {
    throw new LedgerRequestError('contract', answer.status);
  }
}

export async function fetchForecast(path: string, signal?: AbortSignal): Promise<LedgerForecastEnvelope> {
  const answer = await send(path, signal);
  try {
    return parseLedgerForecastEnvelope(answer.body);
  } catch {
    throw new LedgerRequestError('contract', answer.status);
  }
}

interface Answer {
  readonly status: number;
  readonly body: unknown;
}

async function send(path: string, signal?: AbortSignal): Promise<Answer> {
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      ...(signal === undefined ? {} : { signal }),
    });
  } catch (error) {
    if (signal?.aborted === true) throw error;
    throw LedgerRequestError.unreachable();
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    throw new LedgerRequestError(classifyLedgerFailure(response.status, errorCode(body)), response.status, errorCode(body));
  }

  return { status: response.status, body };
}

function errorCode(body: unknown): string | undefined {
  try {
    return parseErrorEnvelope(body).error.code;
  } catch {
    return undefined;
  }
}
